import React, { useState, useEffect } from 'react';
import { ordersAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const AdminOrders = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getAllAdmin();
      setOrders(response.data);
    } catch (error) {
      toast.error(t('admin.orders.fetchError') || 'Failed to load orders', { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      setUpdatingOrderId(orderId);
      await ordersAPI.updateStatus(orderId, newStatus);
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      
      toast.success(t('admin.orders.statusUpdated') || 'Order status updated successfully', { autoClose: 1000 });
    } catch (error) {
      toast.error(t('admin.orders.statusUpdateError') || 'Failed to update order status', { autoClose: 1000 });
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      processing: 'bg-blue-100 text-blue-800 border-blue-300',
      shipped: 'bg-purple-100 text-purple-800 border-purple-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === filterStatus);

  const orderStats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => o.status === 'processing').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Admin - Order Management"
        description="Manage all customer orders, update order status, and track shipments"
        noindex={true}
      />

      <div className="container-custom py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold mb-2">
            {t('admin.orders.title') || 'Order Management'}
          </h1>
          <p className="text-gray-600">
            {t('admin.orders.subtitle') || 'Manage all customer orders and update their status'}
          </p>
        </div>

        {/* Order Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{orderStats.total}</div>
            <div className="text-sm text-gray-600">{t('admin.orders.totalOrders') || 'Total'}</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{orderStats.pending}</div>
            <div className="text-sm text-gray-600">{t('admin.orders.pending') || 'Pending'}</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{orderStats.processing}</div>
            <div className="text-sm text-gray-600">{t('admin.orders.processing') || 'Processing'}</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{orderStats.shipped}</div>
            <div className="text-sm text-gray-600">{t('admin.orders.shipped') || 'Shipped'}</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{orderStats.delivered}</div>
            <div className="text-sm text-gray-600">{t('admin.orders.delivered') || 'Delivered'}</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{orderStats.cancelled}</div>
            <div className="text-sm text-gray-600">{t('admin.orders.cancelled') || 'Cancelled'}</div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition ${
                filterStatus === status
                  ? 'bg-racing-red text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t(`admin.orders.${status}`) || status}
              {status !== 'all' && ` (${orderStats[status]})`}
            </button>
          ))}
        </div>

        {/* Orders Table */}
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 card">
            <p className="text-xl text-gray-600">
              {t('admin.orders.noOrders') || 'No orders found'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-md">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.orders.orderNumber') || 'Order #'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.orders.customer') || 'Customer'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.orders.date') || 'Date'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.orders.total') || 'Total'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.orders.status') || 'Status'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.orders.items') || 'Items'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-mono font-semibold text-sm">{order.order_number}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                      <div className="text-sm text-gray-500">{order.customer_email}</div>
                      <div className="text-xs text-gray-400">{order.customer_phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString()}
                      <div className="text-xs text-gray-400">
                        {new Date(order.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-racing-red">
                        ${order.total_amount.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                        disabled={updatingOrderId === order.id}
                        className={`px-3 py-1 rounded-lg text-sm font-semibold capitalize border-2 cursor-pointer transition ${getStatusColor(order.status)} ${
                          updatingOrderId === order.id ? 'opacity-50 cursor-wait' : 'hover:shadow-md'
                        }`}
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {order.order_items.map((item, idx) => (
                          <div key={item.id} className="mb-1">
                            <span className="font-medium">{item.quantity}x</span> {item.product.name}
                            {idx < order.order_items.length - 1 && ', '}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Shipping Address Section - Expandable in future */}
        <div className="mt-8 card p-6">
          <h3 className="text-lg font-bold mb-4">
            {t('admin.orders.recentActivity') || 'Recent Activity'}
          </h3>
          <div className="text-sm text-gray-600">
            <p>{t('admin.orders.totalOrdersToday') || 'Total orders today'}: <strong>{orders.filter(o => {
              const today = new Date().toDateString();
              return new Date(o.created_at).toDateString() === today;
            }).length}</strong></p>
            <p className="mt-2">{t('admin.orders.pendingOrders') || 'Pending orders'}: <strong className="text-yellow-600">{orderStats.pending}</strong></p>
            <p className="mt-2">{t('admin.orders.processingOrders') || 'Processing orders'}: <strong className="text-blue-600">{orderStats.processing}</strong></p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminOrders;
